import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { FiEdit, FiTrash2, FiRepeat } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  DetailTabBar, DetailRow, DetailStatusPill, ApproverCard, ImageBlock, FeedbackTab, ConfirmModal,
} from "../../components/financeDetail";
import { getLockingByIdApi, deleteLockingApi } from "../../api/finance.locking.api";
import { extractErrorMessage } from "../../api/client";
import { formatMoney } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const TEXT = "#08142D";
const MUTED = "#94A3B8";
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const PartRow = ({ item, isRelative, last }) => {
  const name = item?.employee?.fullName || "-";
  const code = item?.employee?.code || "-";
  const role = isRelative ? (item?.isCollaborator ? "CTV" : "CVKD") : (item?.employeeJobTitle || "-");
  const percent = isRelative ? item?.relativeCommissionRate : item?.commissionRate;
  return (
    <Flex justify="space-between" align="center" gap="12px" py="12px" borderBottom={last ? "none" : "0.5px solid #EEF1F6"}>
      <Text fontSize="14px" color={TEXT} flex="1" noOfLines={2}>{`${name} (${code}) - ${role}`}</Text>
      <Text fontSize="14px" fontWeight={600} color={TEXT}>{`${percent || 0}%`}</Text>
    </Flex>
  );
};

const ParticipantTab = ({ data }) => {
  const grouped = useMemo(() => {
    const map = new Map();
    (data?.lockingRelativeParticipant ?? []).forEach((it) => {
      const key = it.commissionRate || 0;
      map.set(key, [...(map.get(key) ?? []), it]);
    });
    return Array.from(map.entries());
  }, [data]);
  const participants = data?.lockingParticipant || [];

  return (
    <Box pb="20px">
      <Box mt="4px">
        <Text fontSize="15px" fontWeight={700} color={TEXT}>Thành viên tham gia giao dịch</Text>
        <Text fontSize="12px" color="#4081EB" mt="2px">( Thông tin này được cập nhật khi tạo locking, không bao gồm cộng tác viên )</Text>
        {participants.length ? (
          <Box mt="12px" border="1px solid" borderColor="#E1E1E1" borderRadius="10px" px="12px">
            {participants.map((it, i) => <PartRow key={it._id || i} item={it} last={i === participants.length - 1} />)}
          </Box>
        ) : <Text mt="8px" fontSize="14px" color={MUTED}>Chưa có nhân viên tham gia giao dịch</Text>}
      </Box>

      <Box mt="20px">
        <Text fontSize="15px" fontWeight={700} color={TEXT}>Thành viên chốt giao dịch</Text>
        <Text fontSize="12px" color="#4081EB" mt="2px">( Thông tin này được cập nhật khi locking được duyệt, bao gồm cả cộng tác viên, ảnh hưởng trực tiếp đến phí dịch vụ )</Text>
        {grouped.length ? grouped.map(([rate, items]) => (
          <Box key={rate} mt="12px" border="1px solid" borderColor="#E1E1E1" borderRadius="10px" px="12px">
            {items.map((it, i) => <PartRow key={it._id || i} item={it} isRelative last={i === items.length - 1} />)}
          </Box>
        )) : <Text mt="8px" fontSize="14px" color={MUTED}>Chưa có thành viên chốt giao dịch</Text>}
      </Box>
    </Box>
  );
};

const LockingDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [showDelete, setShowDelete] = useState(false);

  const load = () => {
    setLoading(true);
    getLockingByIdApi(id).then((d) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const tabs = useMemo(() => {
    const r = [{ key: "overview", title: "Tổng quan" }];
    if (data?.images?.length) r.push({ key: "transaction", title: "Giao dịch" });
    if (data?.lockingParticipant?.length) r.push({ key: "participant", title: "Thành viên" });
    if (data?._id) r.push({ key: "interactNote", title: "Phản hồi" });
    return r;
  }, [data]);
  useEffect(() => { if (tab >= tabs.length) setTab(0); }, [tabs.length, tab]);
  const activeKey = tabs[tab]?.key || "overview";

  const st = data?.status;
  const isNew = st === "NEW";
  const isApproved = st === "APPROVED";
  const relativeApproved = data?.registerRelativeStatus === "APPROVED";

  const doDelete = async () => {
    setShowDelete(false);
    try { await deleteLockingApi(id); toast.success("Xóa locking thành công"); navigate(`${EMP_BASE_PATH}/tai-chinh/locking`); }
    catch (e) { toast.error(extractErrorMessage(e, "Xóa locking thất bại")); }
  };

  const outlineBtn = { variant: "outline", borderColor: "#DCE1EA", color: TEXT, borderRadius: "8px", h: "48px", _hover: { bg: "#F7F9FC" } };
  const primaryBtn = { bg: APP.primary200, color: "white", borderRadius: "8px", h: "48px", _hover: { bg: APP.primary100 } };

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : !data ? (
        <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy thông tin locking</Text></Flex>
      ) : (
        <>
          <DetailTabBar tabs={tabs} index={tab} onChange={setTab} />

          <Box flex="1" overflowY="auto" px="16px" pt="16px">
            {activeKey === "overview" && (
              <Box pb="20px">
                <Text fontSize="16px" fontWeight={600} color={TEXT} noOfLines={2}>
                  {data?.project?.name || ""}{data?.project?.name ? " - " : ""}{data?.apartment?.code || "Không định danh"}
                </Text>
                <Box mt="12px"><DetailStatusPill status={st} /></Box>
                <ApproverCard approver={data?.lockingHistory?.approver} dateLabel={fmtD(data?.createdAt)} />

                <Box mt="8px">
                  <DetailRow label="Dự án" value={data?.project?.name} onPress={data?.projectId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an/${data.projectId}`) : undefined} />
                  <DetailRow label="Đợt mở bán" value={data?.saleOpeningEvent?.name} />
                  {data?.booking?.code && <DetailRow label="Mã booking" value={data.booking.code} />}
                  <DetailRow label="Căn hộ" value={data?.apartment?.code ? `${data.apartment.code} (${data?.apartment?.name || ""})` : "-"} />
                  <DetailRow label="Giá trị niêm yết" value={formatMoney(data?.apartment?.totalValue || 0)} />
                  <DetailRow label="Khách hàng" value={data?.customer?.name} onPress={data?.customerId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/${data.customerId}`) : undefined} />
                  <DetailRow label="Trạng thái booking" value={data?.bookingId ? "Xem chi tiết booking" : "-"} onPress={data?.bookingId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/booking/${data.bookingId}`) : undefined} />
                  <DetailRow label="Ngày vào cọc" value={fmtD(data?.bookingTime)} />
                  <DetailRow label="Ngày tạo locking" value={fmtD(data?.createdAt)} />
                  <DetailRow label="Số tiền đặt cọc" value={formatMoney(data?.price || 0)} color={APP.primary200} />
                  <DetailRow label="Ghi chú" value={data?.note} last />
                </Box>
              </Box>
            )}

            {activeKey === "transaction" && (
              <Box pb="20px"><ImageBlock images={data?.images} emptyText="Chưa có ảnh ủy nhiệm chi" /></Box>
            )}

            {activeKey === "participant" && <ParticipantTab data={data} />}

            {activeKey === "interactNote" && (
              <Box pb="20px"><FeedbackTab query={{ lockingId: id }} /></Box>
            )}
          </Box>

          {/* Hành động tab Tổng quan */}
          {tab === 0 && (isNew || isApproved) && (
            <Box borderTop="1px solid #EEF1F6" p="16px">
              {isNew && (
                <Flex gap="16px">
                  <Button flex="1" {...outlineBtn} leftIcon={<FiEdit />} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/locking/tao?id=${id}`)}>Sửa</Button>
                  <Button flex="1" {...outlineBtn} leftIcon={<FiTrash2 />} onClick={() => setShowDelete(true)}>Xóa</Button>
                </Flex>
              )}
              {isApproved && (
                <Flex gap="16px" wrap="wrap">
                  {data?.project?.is_allow_ttkq && !data?.statusTtkq && (
                    <Button flex="1" minW="45%" {...outlineBtn} onClick={() => navigate(`${EMP_BASE_PATH}/bao-tri`)}>Lên TTKQ</Button>
                  )}
                  {!data?.project?.is_allow_ttkq && !data?.statusSalesContract && (
                    <Button flex="1" minW="45%" {...outlineBtn} onClick={() => navigate(`${EMP_BASE_PATH}/bao-tri`)}>Lên HDMB</Button>
                  )}
                  <Button flex="1" minW="45%" {...outlineBtn} leftIcon={<FiRepeat />} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/locking/chuyen/${id}`)}>Chuyển locking</Button>
                </Flex>
              )}
            </Box>
          )}

          {/* Hành động tab Thành viên */}
          {activeKey === "participant" && isApproved && (
            <Box borderTop="1px solid #EEF1F6" p="16px">
              <Button
                w="100%"
                {...primaryBtn}
                onClick={() =>
                  navigate(
                    `${EMP_BASE_PATH}/tai-chinh/locking/${id}/ctv${relativeApproved ? "?type=detail" : ""}`
                  )
                }
              >
                {relativeApproved ? "Chi tiết CTV tham gia" : "Cập nhật CTV tham gia"}
              </Button>
            </Box>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={showDelete}
        title="Xóa phiếu"
        message={`Bạn có chắc chắn muốn xóa locking ${data?.apartment?.name || data?.code || ""}?`}
        confirmText="Xóa"
        onConfirm={doDelete}
        onClose={() => setShowDelete(false)}
      />
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết đơn đăng ký locking" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Chi tiết đơn đăng ký locking" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/locking`)} />
      {pageBody}
    </Box>
  );
};

export default LockingDetail;
